import React, { useRef, useState } from 'react'
import { Button } from '@surecompanies/core_components'
import ContentSection from 'layouts/ContentSection/ContentSection'
import ContentPane from './ContentPane'
import EnrollmentConfigurationCard from './EnrollmentConfigurationCard'
import EnrollmentForm, { FormRef } from './EnrollmentForm'
import useEnrollmentConfiguration from '../hooks/useEnrollmentConfiguration'
import usePayrollDownloadUrls from 'routes/Company/hooks/usePayrollDownloadUrls'
import PauseEnrollmentModal from './PauseEnrollmentModal'
import ResumeEnrollmentModal from './ResumeEnrollmentModal'

export default function EnrollmentConfiguration({ id }: { id: string }) {
  const {
    onNewEnrollmentConfiguration,
    isCreating,
    isLoading,
    isCreatingOrUpdating,
    createOrUpdateEnrollmentConfiguration,
    getEnrollmentConfigurationQuery,
    pauseEnrollment,
    isPausing,
    resumeEnrollment,
    isResuming,
  } = useEnrollmentConfiguration(id)

  const {
    data: payrollDownloadUrlsData,
    isLoading: isLoadingPayrollDownloadUrls,
  } = usePayrollDownloadUrls(id)

  const [resumeModalForId, setResumeModalForId] = useState<string | null>(null)
  const [pauseModalForId, setPauseModalForId] = useState<string | null>(null)

  const formRef = useRef<FormRef>(null)
  const data = getEnrollmentConfigurationQuery.data

  // Create a map of effective dates to download URLs for quick lookup
  const downloadUrlMap = React.useMemo(() => {
    const map = new Map<string, string>()
    payrollDownloadUrlsData?.downloadUrls?.forEach((url) => {
      map.set(url.effectiveDate, url.downloadUrl)
    })
    return map
  }, [payrollDownloadUrlsData])

  const currentYear = new Date().getFullYear()
  return (
    <ContentSection
      loading={isLoading}
      title="Enrollment Configuration"
      actions={
        <Button
          variant={isCreating ? 'outlined' : 'solid'}
          type={isCreating ? 'default' : 'primary'}
          onClick={onNewEnrollmentConfiguration}
        >
          {isCreating ? 'Cancel' : 'Add New'}
        </Button>
      }
    >
      {isCreating && (
        <ContentPane
          title="Add New Enrollment"
          header={
            <Button
              variant="solid"
              type="primary"
              onClick={() => formRef?.current?.submit()}
              loading={isCreatingOrUpdating}
            >
              Save
            </Button>
          }
        >
          <EnrollmentForm
            ref={formRef}
            loading={isCreatingOrUpdating}
            onFormSubmit={createOrUpdateEnrollmentConfiguration}
          />
        </ContentPane>
      )}
      {data?.map((enrollment) => (
        <EnrollmentConfigurationCard
          key={enrollment.id?.toString()}
          id={id}
          enrollment={enrollment}
          currentYear={currentYear}
          downloadUrl={downloadUrlMap.get(enrollment.benefitEffectiveDate)}
          isLoadingDownloadUrls={isLoadingPayrollDownloadUrls}
          isResuming={isResuming}
          isPausing={isPausing}
          onResumeClick={setResumeModalForId}
          onPauseClick={setPauseModalForId}
        />
      ))}
      <PauseEnrollmentModal
        open={!!pauseModalForId}
        confirmLoading={isPausing}
        onConfirm={() => {
          if (pauseModalForId) {
            pauseEnrollment(pauseModalForId, {
              onSuccess: () => setPauseModalForId(null),
            })
          }
        }}
        onClose={() => setPauseModalForId(null)}
      />
      <ResumeEnrollmentModal
        open={!!resumeModalForId}
        confirmLoading={isResuming}
        onConfirm={(clearCarts) => {
          if (resumeModalForId) {
            resumeEnrollment(
              { enrollmentId: resumeModalForId, clearCarts },
              {
                onSuccess: () => setResumeModalForId(null),
                onError: (error) => {
                  if (error?.response?.status === 409) {
                    setResumeModalForId(null)
                  }
                },
              }
            )
          }
        }}
        onClose={() => setResumeModalForId(null)}
      />
    </ContentSection>
  )
}
